import React, { useState } from 'react';
import { Box, Button, CircularProgress, Paper, IconButton, Typography, List, ListItem, ListItemAvatar, Avatar, ListItemText, LinearProgress } from '@mui/material';
import { Upload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { storage } from '../../config/firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';

interface MultipleImageUploadFieldProps {
  label: string;
  value: string[];
  onChange: (urls: string[]) => void;
  storagePath: string;
  error?: boolean;
  helperText?: string;
}

const MultipleImageUploadField: React.FC<MultipleImageUploadFieldProps> = ({ label, value, onChange, storagePath, error, helperText }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileName, setCurrentFileName] = useState('');

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      handleUpload(Array.from(event.target.files));
    }
  };

  const handleUpload = async (files: File[]) => {
    setUploading(true);
    setUploadProgress(0);

    const compressionOptions = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    const uploadPromises = files.map(async (file, index) => {
      try {
        console.log(`Original file size ${file.size / 1024 / 1024} MB`);
        const compressedFile = await imageCompression(file, compressionOptions);
        console.log(`Compressed file size ${compressedFile.size / 1024 / 1024} MB`);

        const fileId = uuidv4();
        const storageRef = ref(storage, `${storagePath}/${fileId}_${compressedFile.name}`);
        
        // uploadBytesResumable kullanarak progress tracking
        const uploadTask = uploadBytesResumable(storageRef, compressedFile);

        return new Promise<string>((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              // Progress tracking
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              const fileProgress = ((index + progress / 100) / files.length) * 100;
              setUploadProgress(fileProgress);
              setCurrentFileName(file.name);
              console.log(`Upload ${progress.toFixed(1)}% done for ${file.name}`);
            },
            (error) => {
              // Error handling
              console.error('Upload error:', error);
              reject(error);
            },
            async () => {
              // Upload completed successfully
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                resolve(downloadURL);
              } catch (error) {
                reject(error);
              }
            }
          );
        });

      } catch (error) {
        console.error('Image compression error:', error);
        throw error;
      }
    });

    try {
      const newUrls = await Promise.all(uploadPromises);
      onChange([...value, ...newUrls]);
      setCurrentFileName('');
    } catch (error) {
      console.error("One or more uploads failed", error);
      alert('Bir veya daha fazla dosya yüklenirken hata oluştu.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveImage = (urlToRemove: string) => {
    onChange(value.filter(url => url !== urlToRemove));
  };

  return (
    <Box sx={{mt: 2}}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: error ? 'error.main' : 'divider' }}>
        <Typography variant="subtitle1" gutterBottom>{label}</Typography>
        <List dense>
          {value.map((url, index) => (
            <ListItem
              key={index}
              secondaryAction={
                <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveImage(url)}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemAvatar>
                <Avatar src={url} variant="rounded" />
              </ListItemAvatar>
              <ListItemText 
                primary={`Görsel ${index + 1}`} 
                secondary={url.substring(0, 30) + '...'}
                sx={{ '& .MuiListItemText-secondary': {overflow: 'hidden', textOverflow: 'ellipsis'} }}
              />
            </ListItem>
          ))}
        </List>
        <Button
          variant="contained"
          component="label"
          startIcon={<UploadIcon />}
          disabled={uploading}
          fullWidth
        >
          {uploading ? 'Yükleniyor...' : 'Yeni Görsel Yükle'}
          <input type="file" hidden multiple accept="image/*" onChange={handleFileChange} />
        </Button>
        {helperText && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{helperText}</Typography>}
        {uploading && (
           <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">
                  {currentFileName ? `${currentFileName} yükleniyor...` : 'Görseller yükleniyor...'}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ borderRadius: 1 }}
              />
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                %{uploadProgress.toFixed(1)} tamamlandı
              </Typography>
           </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MultipleImageUploadField; 